import { instanceSchedule } from "../../../../services/instances";
import type { SelectOption } from "../types/hooks";
import type {
  Alert,
  CreateEmployeeScheduleRules,
  DeleteEmployeeScheduleRule,
  GetEmployeeScheduleRules,
  SchedulingRule,
  SchedulingRuleAssignment,
  UpdateEmployeeScheduleRule,
} from "../types/services";

type SchedulingRuleResponse = { data?: SchedulingRule[] } | SchedulingRule[];

const normalizeRuleId = (rule: SchedulingRule) =>
  rule.ruleset_id ?? rule.scheduling_rule_id ?? rule.id ?? 0;

const unwrapRules = (response: SchedulingRuleResponse): SchedulingRule[] => {
  if (Array.isArray(response)) return response;
  return response.data ?? [];
};

const getScheduleRules = async ({ alert }: { alert: Alert }) => {
  const { showAlert, t } = alert;
  const fallbackUrls = ["/ruleset", "/rulesets", "/scheduling_rules"];

  for (const url of fallbackUrls) {
    try {
      const response = await instanceSchedule.get(url);
      const rules = unwrapRules(response.data).map((rule) => ({
        value: String(normalizeRuleId(rule)),
        label: rule.name,
        sublabel: rule.description || "",
      }));

      return {
        rules: rules.sort((a: SelectOption, b: SelectOption) =>
          a.label.localeCompare(b.label),
        ),
      };
    } catch (error) {
      console.error(error);
    }
  }

  showAlert("error", t("assign_schedule_rule.alerts.load_error"));
  return { rules: [] as SelectOption[] };
};

const getEmployeeScheduleRules = async ({
  employeeId,
  alert,
}: GetEmployeeScheduleRules): Promise<{ assignments: SchedulingRuleAssignment[] }> => {
  const { showAlert, t } = alert;

  try {
    const response = await instanceSchedule.get("/ruleset-assignment", {
      params: { employee_id: Number(employeeId) },
    });

    return { assignments: response.data.data ?? [] };
  } catch (error) {
    console.error(error);
    showAlert("error", t("assign_schedule_rule.alerts.load_error"));
    return { assignments: [] };
  }
};

const createEmployeeScheduleRules = async ({
  body,
  alert,
}: CreateEmployeeScheduleRules): Promise<boolean> => {
  const { showAlert, t } = alert;

  try {
    await instanceSchedule.post("/ruleset-assignment", body);
    return true;
  } catch (error) {
    console.error(error);
    showAlert("error", t("assign_schedule_rule.alerts.error"));
    return false;
  }
};

const updateEmployeeScheduleRule = async ({
  assignmentId,
  body,
  alert,
}: UpdateEmployeeScheduleRule): Promise<boolean> => {
  const { showAlert, t } = alert;

  try {
    await instanceSchedule.patch(`/ruleset-assignment/${assignmentId}`, body);
    return true;
  } catch (error) {
    console.error(error);
    showAlert("error", t("assign_schedule_rule.alerts.error"));
    return false;
  }
};

const deleteEmployeeScheduleRule = async ({
  assignmentId,
  alert,
}: DeleteEmployeeScheduleRule): Promise<boolean> => {
  const { showAlert, t } = alert;

  try {
    await instanceSchedule.delete(`/ruleset-assignment/${assignmentId}`);
    return true;
  } catch (error) {
    console.error(error);
    showAlert("error", t("assign_schedule_rule.alerts.error"));
    return false;
  }
};

export {
  getScheduleRules,
  getEmployeeScheduleRules,
  createEmployeeScheduleRules,
  updateEmployeeScheduleRule,
  deleteEmployeeScheduleRule,
};